import { getIsStatus } from '@/Components/posts/status';
import { Footer } from '@/Components/templates/Footer';
import { HeaderDesktop, HeaderMobile } from '@/Components/templates/header';
import { useBreakpoints, useUser } from '@/Utility/hooks';
import { PostStatusEnum } from '@/graphql/types';
import { CircularProgress, Container, type ContainerProps, type GridProps, Stack } from '@mui/material';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppStore } from '../AppStore';
import TopBar from './TopBar/TopBar';

export const PAGE_LAYOUT = {
  leftProps: {
    size: {
      xs: 12,
      md: 8,
    },
  } as GridProps,
  rightProps: {
    size: {
      xs: 12,
      md: 4,
    },
  } as GridProps,
  centerProps: {
    spacing: 2,
    pr: {
      xs: 0.5,
      md: 5,
    },
    pl: {
      xs: 0.5,
      md: 5,
    },
  } as GridProps,
  containerProps: {
    maxWidth: 'xl',
    sx: {},
  } as ContainerProps,
};

type Props = {
  children: React.ReactNode;
  className?: string;
};

const Page = (props: Props) => {
  const showFooter = useAppStore((state) => state.showFooter);
  const showHeader = useAppStore((state) => state.showHeader);
  const showTopBar = useAppStore((state) => state.showTopBar);
  const playerNode = useAppStore((state) => state.playerNode);
  const znglist = useAppStore((state) => state.znglist);
  const hub = useAppStore((state) => state.hub);
  const { userExists } = useUser();

  const { isMobile } = useBreakpoints();

  const [showAlert, setShowAlert] = useState(true);

  const Header = useCallback(() => {
    if (!showHeader) return null;

    if (isMobile) return <HeaderMobile />;
    return <HeaderDesktop />;
  }, [isMobile, showHeader]);

  const alertStyle = {
    backgroundColor: 'var(--z-red-clr-1)',
    color: 'white',
    padding: '0.5em 1em',
    width: '100%',
  };

  const hasAlerts = useMemo(() => {
    const alerts = [];

    if (getIsStatus(playerNode?.status, PostStatusEnum.Draft)) {
      alerts.push('This catalog post is currently in draft mode.');
    }
    if (getIsStatus(znglist?.status, PostStatusEnum.Draft)) {
      alerts.push('This znglist page is currently in draft mode.');
    }
    if (getIsStatus(hub?.status, PostStatusEnum.Draft)) {
      alerts.push('This hub is currently in draft mode.');
    }

    let statusSettings = hub?.statusSettings || znglist?.statusSettings;
    if (!!hub?.statusSettings) {
      statusSettings = hub.statusSettings;
    }
    if (!!znglist?.statusSettings) {
      statusSettings = znglist.statusSettings;
    }

    return alerts;
  }, [playerNode?.status, znglist, hub, userExists]);

  return (
    <>
      <Header />

      {hasAlerts.length > 0 && showAlert && userExists && (
        <Stack
          direction="column"
          justifyContent="center"
          alignItems="center"
          sx={{
            position: 'relative',
            width: '100%',
          }}
          onClick={() => setShowAlert(false)}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              zIndex: 1000,
              backgroundColor: 'var(--z-red-clr-1)',
              color: 'white',
              padding: '0.5em 1em',
              cursor: 'pointer',
            }}
          >
            x
          </div>
          {hasAlerts.map((alert, idx) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
            <div key={alert + idx} style={alertStyle}>
              {alert}
            </div>
          ))}
        </Stack>
      )}

      <Container maxWidth="xl" sx={{ minHeight: '60vh' }} className={`${props?.className ?? ''} z-frontend-body`}>
        {showTopBar && <TopBar />}
        {props.children}
      </Container>

      {showFooter && <Footer />}
    </>
  );
};

export const useNoHeader = () => {
  useEffect(() => {
    useAppStore.setState({ showHeader: false });

    return () => {
      useAppStore.setState({ showHeader: true });
    };
  }, []);
};

type ZnglyAppFallbackProps = {
  bodyOnly?: boolean;
};

// standard fallback while the data is loading
export const ZnglyAppFallback = ({ bodyOnly }: ZnglyAppFallbackProps) => {
  const { isMobile } = useBreakpoints();

  const body = (
    <Container maxWidth="xl" sx={{ minHeight: '60vh' }}>
      <Stack direction="row" justifyContent="center" alignItems="center" sx={{ minHeight: '90vh' }}>
        <CircularProgress sx={{ color: 'var(--page-p-accent-col)' }} />
      </Stack>
    </Container>
  );

  if (bodyOnly) return body;

  return (
    <>
      {isMobile ? <HeaderMobile /> : <HeaderDesktop />}

      {body}

      <Footer />
    </>
  );
};

export default Page;
